import React, { useState, useMemo } from "react";
import { useJobsQuery, useDeleteJobMutation } from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
} from "@hugeicons/core-free-icons";
import { Briefcase06Icon } from "@hugeicons/core-free-icons/index";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";

const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={Briefcase06Icon}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_JOBS_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_JOBS_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={Briefcase06Icon}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_JOB")}
        </Link>
      )}
    </div>
  );
};

export const Jobs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    jobId: null,
  });
  const location = useLocation();
  const basePath = location.pathname.includes("seasonal_hr")
    ? "/app/seasonal_hr/basic-data/jobs"
    : "/app/makkah/basic-data/jobs";

  const { data, isLoading, isFetching } = useJobsQuery({
    page: currentPage,
    per_page: 10,
  });
  const jobs = data?.data?.data ?? [];
  const meta = data?.data?.meta ?? data?.meta ?? {};
  const { filteredData, ...restClientSearch } = useClientSearch(jobs);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const loading = isLoading || isFetching;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const pagination =
    meta?.total != null && meta.total > 0
      ? {
          current: meta?.current_page ?? currentPage,
          total: meta.total,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
        }
      : undefined;

  const { mutate: deleteJob, isPending: isDeleting } = useDeleteJobMutation();

  const handleDeleteClick = (jobId) => {
    setDeleteModalState({ isOpen: true, jobId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, jobId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.jobId) {
      deleteJob(deleteModalState.jobId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, jobId: null });
          queryClient.invalidateQueries({ queryKey: ["jobs"] });
        },
      });
    }
  };

  const columns = useMemo(
    () => [
      { name: t("NAME_AR"), key: "name_ar", filterable: true },
      { name: t("NAME_EN"), key: "name_en", filterable: true },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  const getActions = (rowData) => [
    {
      key: "edit",
      label: (
        <Link
          to={`${basePath}/${rowData.id}/edit`}
          className="d-flex align-items-center gap-2"
        >
          <HugeiconsIcon size={16} icon={Edit02Icon} />
          {t("EDIT")}
        </Link>
      ),
    },
    { type: "divider" },
    {
      key: "delete",
      label: (
        <div className="d-flex align-items-center gap-2">
          <HugeiconsIcon size={16} icon={DeleteIcon} />
          {t("DELETE")}
        </div>
      ),
      onClick: () => handleDeleteClick(rowData.id),
      danger: true,
    },
  ];

  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_JOB")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        {...restClientSearch}
        columns={columns}
        loading={loading}
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
        pagination={pagination}
      />

      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_JOB"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};
